/** Lists direct studies and retained cleanup receipts with current server-derived status. */
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { shopifyQueryPolicies } from '../../queries/shopifyQueryPolicies'
import { shopifyComponentCopy } from '@testfeed/product-language'
import { getAudienceFrameLabel } from '../../components/study-setup-card-helpers'
import type { ShopifyAppBridge } from '../../shopifyApiFetch'
import type { VerifiedTestFeedConnectionScope } from '../../ShopifyAppContext'
import { listStudyAuthoring } from './studyAuthoringClient'
import { trackShopifyAction } from '../../shopifyUiTracking'
import { cachedStudyDeletionReceipts, projectStudyDeletionReceipts } from './useStudyDeletionReceipts'
import { studyAuthoringQueryPrefix } from './useStudyAuthoringEditor'
import type { SavedStudyBuilderDraftSummary } from './studyBuilderDrafts'

export async function fetchStudyAuthoringSummaries(shopify: ShopifyAppBridge, signal: AbortSignal) {
  const summaries: SavedStudyBuilderDraftSummary[] = []
  let beforeStudyId: string | undefined
  const seen = new Set<string>()
  do {
    // The index needs all rows for its existing filters/counts; use the supported page cap,
    // while the server retains its byte bound and continuation cursor.
    const page = await listStudyAuthoring(shopify, { limit: 100, beforeStudyId, includeState: true }, signal)
    // Status uses the shared server projector; a failed read is never displayed as Draft.
    for (const item of page.studies) {
      if (seen.has(item.studyId)) throw new Error('STUDY_AUTHORING_INVALID: Repeated study in list')
      seen.add(item.studyId)
      const authoring = item
      const state = page.states?.[item.studyId]
      if (!state || state.revision !== item.revision)
        throw new Error('STUDY_AUTHORING_INVALID: Missing current study state')
      const summary = page.summaries?.[item.studyId]
      if (!summary || summary.revision !== item.revision || summary.runId !== state.latestRunId)
        throw new Error('STUDY_AUTHORING_INVALID: Missing current Tests summary')
      const product = authoring.draft.selectedProduct
      summaries.push({
        draftId: authoring.studyId,
        studyId: authoring.studyId,
        state,
        summary,
        treatmentType: summary.treatmentType ?? undefined,
        versionCount: summary.versionCount,
        audience: {
          value: `frame:${authoring.draft.selectedAudienceMode}`,
          label: getAudienceFrameLabel(authoring.draft.selectedAudienceMode),
        },
        title: authoring.draft.briefDraft.studyName || shopifyComponentCopy.studyBuilderPage.untitledTest,
        productId: product?.id,
        productTitle: product?.title ?? null,
        productImageUrl: product?.imageUrl,
        productImageAlt: product?.imageAlt,
        createdAt: authoring.createdAt,
        updatedAt: authoring.updatedAt,
        source: 'saved',
      })
    }
    for (const receipt of page.deletions ?? []) {
      if (receipt.status === 'completed') continue
      if (!receipt.acceptedAt || seen.has(receipt.studyId))
        throw new Error('STUDY_AUTHORING_INVALID: Invalid retained deletion receipt')
      seen.add(receipt.studyId)
      summaries.push({
        draftId: receipt.studyId,
        studyId: receipt.studyId,
        deletion: receipt,
        title: shopifyComponentCopy.studyBuilderPage.deletionTitle,
        productTitle: null,
        createdAt: receipt.acceptedAt,
        updatedAt: receipt.acceptedAt,
        source: 'saved',
      })
    }
    if (page.nextCursor && page.nextCursor === beforeStudyId)
      throw new Error('STUDY_AUTHORING_INVALID: Repeated list cursor')
    beforeStudyId = page.nextCursor ?? undefined
  } while (beforeStudyId)
  return summaries
}
export function useStudyAuthoringList(shopify: ShopifyAppBridge, scope: VerifiedTestFeedConnectionScope | null) {
  const client = useQueryClient()
  return useQuery({
    ...shopifyQueryPolicies.studyAuthoring,
    queryKey: [...(scope ? studyAuthoringQueryPrefix(scope) : ['shopify-study-authoring', 'no-scope']), 'list'],
    enabled: Boolean(scope),
    retry: false,
    queryFn: ({ signal }) =>
      trackShopifyAction(shopify, 'load-tests', async () => {
        const rows = await fetchStudyAuthoringSummaries(shopify, signal)
        return scope ? projectStudyDeletionReceipts(rows, cachedStudyDeletionReceipts(client, scope)) : rows
      }),
    refetchInterval: (query) =>
      query.state.data?.some((item) => item.state?.status === 'running' || item.state?.status === 'preparing')
        ? 5000
        : false,
  })
}
